import { useCallback } from 'react'

import { Reward, RewardCategory } from '@/features/store/types/reward'

import { shouldSendOnce, sample } from './shouldSendOnce'
import { useAnalytics } from './useAnalytics'

export function useStoreAnalytics() {
  const { track } = useAnalytics()

  const trackStoreCategoryViewed = useCallback(
    (category: RewardCategory) => {
      // TTL de 30 minutos para viewed events
      if (
        shouldSendOnce('store_category_viewed', category.id, 30 * 60 * 1000)
      ) {
        track.event('store_category_viewed', {
          category_id: category.id,
          category_name: category.name.slice(0, 50), // Limitado
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackRewardViewed = useCallback(
    (reward: Reward) => {
      // TTL de 30 minutos para viewed events
      if (shouldSendOnce('reward_viewed', reward.id, 30 * 60 * 1000)) {
        track.event('reward_viewed', {
          reward_id: reward.id,
          reward_name: reward.name.slice(0, 50), // Limitado
          category_name: reward.reward_category?.name?.slice(0, 50),
          coin_cost: reward.reward_items?.[0]?.coin || 0,
          max_quantity_per_user: reward.max_quantity_per_user,
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackStorePurchaseAttempted = useCallback(
    (reward: Reward, quantity: number, isCheckoutStart = false) => {
      if (isCheckoutStart) {
        const coinCost = reward.reward_items?.[0]?.coin || 0
        track.sampled('store_purchase_attempted', 0.2, {
          reward_id: reward.id,
          reward_name: reward.name.slice(0, 50),
          quantity,
          coin_cost: coinCost,
          total_cost: coinCost * quantity,
          context_route: window.location.pathname
        })
      }
    },
    [track]
  )

  const trackStorePurchaseCompleted = useCallback(
    (reward: Reward, quantity: number) => {
      const coinCost = reward.reward_items?.[0]?.coin || 0
      track.event('store_purchase_completed', {
        reward_id: reward.id,
        reward_name: reward.name.slice(0, 50),
        quantity,
        coin_cost: coinCost,
        total_cost: coinCost * quantity,
        context_route: window.location.pathname
      })
    },
    [track]
  )

  const trackStorePurchaseFailed = useCallback(
    (
      reward: Reward,
      quantity: number,
      reason:
        | 'out_of_stock'
        | 'insufficient_coins'
        | 'max_requests_reached'
        | 'unknown'
    ) => {
      const coinCost = reward.reward_items?.[0]?.coin || 0
      track.event('store_purchase_failed', {
        reward_id: reward.id,
        reward_name: reward.name.slice(0, 50),
        quantity,
        coin_cost: coinCost,
        total_cost: coinCost * quantity,
        failure_reason: reason,
        context_route: window.location.pathname
      })
    },
    [track]
  )

  return {
    trackStoreCategoryViewed,
    trackRewardViewed,
    trackStorePurchaseAttempted,
    trackStorePurchaseCompleted,
    trackStorePurchaseFailed
  }
}
